import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default placeholder logos
const defaultLogos = [
  { id: '1', name: 'Client 1', url: 'https://placehold.co/200x80/transparent/white?text=CLIENT+1&font=montserrat', order: 0 },
  { id: '2', name: 'Client 2', url: 'https://placehold.co/200x80/transparent/white?text=CLIENT+2&font=montserrat', order: 1 },
  { id: '3', name: 'Client 3', url: 'https://placehold.co/200x80/transparent/white?text=CLIENT+3&font=montserrat', order: 2 },
  { id: '4', name: 'Client 4', url: 'https://placehold.co/200x80/transparent/white?text=CLIENT+4&font=montserrat', order: 3 },
  { id: '5', name: 'Client 5', url: 'https://placehold.co/200x80/transparent/white?text=CLIENT+5&font=montserrat', order: 4 },
  { id: '6', name: 'Client 6', url: 'https://placehold.co/200x80/transparent/white?text=CLIENT+6&font=montserrat', order: 5 },
];

export default function ClientLogos({ logos = [] }) {
  const containerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);
  
  // Sort logos by order field
  const sortedLogos = [...(logos.length > 0 ? logos : defaultLogos)].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleMouseDown = (e) => {
    setIsDragging(true);
    dragStartX.current = e.clientX;
    scrollStartX.current = containerRef.current?.scrollLeft || 0;
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    const delta = e.clientX - dragStartX.current;
    containerRef.current.scrollLeft = scrollStartX.current - delta;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setIsPaused(true);
    dragStartX.current = e.touches[0].clientX;
    scrollStartX.current = containerRef.current?.scrollLeft || 0;
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !containerRef.current) return;
    const delta = e.touches[0].clientX - dragStartX.current;
    containerRef.current.scrollLeft = scrollStartX.current - delta;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setIsPaused(false);
  };

  return (
    <section className="py-16 md:py-20 bg-background overflow-hidden">
      <div className="container-custom mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="kicker">
            Trusted by
          </span>
        </motion.div>
      </div>

      {/* Carousel wrapper */}
      <div className="relative">
        {/* Left gradient fade */}
        <div 
          className="absolute left-0 top-0 bottom-0 w-20 md:w-32 lg:w-48 z-10 pointer-events-none"
          style={{
            background: 'linear-gradient(to right, hsl(var(--background)) 0%, transparent 100%)'
          }}
        />
        
        {/* Right gradient fade */}
        <div 
          className="absolute right-0 top-0 bottom-0 w-20 md:w-32 lg:w-48 z-10 pointer-events-none"
          style={{
            background: 'linear-gradient(to left, hsl(var(--background)) 0%, transparent 100%)'
          }}
        />

        {/* Scrollable container for drag */}
        <div 
          ref={containerRef}
          className="overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => { setIsPaused(false); setIsDragging(false); }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Scroll track */}
          <div 
            className="logo-carousel-track"
            style={{
              animationPlayState: (isPaused || isDragging) ? 'paused' : 'running'
            }}
          >
            {/* First set */}
            {sortedLogos.map((logo, index) => (
              <div key={`a-${logo.id}-${index}`} className="logo-item">
                <img
                  src={logo.url.startsWith('/api') ? `${API_URL}${logo.url}` : logo.url}
                  alt={logo.name}
                  draggable={false}
                />
              </div>
            ))}
            {/* Second set for seamless loop */}
            {sortedLogos.map((logo, index) => (
              <div key={`b-${logo.id}-${index}`} className="logo-item">
                <img
                  src={logo.url.startsWith('/api') ? `${API_URL}${logo.url}` : logo.url}
                  alt={logo.name}
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .logo-carousel-track {
          display: flex;
          width: max-content;
          animation: logoScroll 35s linear infinite;
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        .overflow-x-auto {
          cursor: grab;
        }
        
        .overflow-x-auto:active {
          cursor: grabbing;
        }
        
        .logo-item {
          flex-shrink: 0;
          padding: 1rem 2rem;
        }
        
        @media (min-width: 768px) {
          .logo-item {
            padding: 1rem 3rem;
          }
        }
        
        @media (min-width: 1024px) {
          .logo-item {
            padding: 1rem 4rem;
          }
        }
        
        .logo-item img {
          height: 4rem;
          width: auto;
          object-fit: contain;
          opacity: 0.45;
          transition: opacity 0.5s ease, filter 0.5s ease;
          filter: brightness(0);
          user-select: none;
        }
        
        @media (min-width: 768px) {
          .logo-item img {
            height: 5rem;
          }
        }
        
        @media (min-width: 1024px) {
          .logo-item img {
            height: 6rem;
          }
        }
        
        .logo-item:hover img {
          opacity: 1;
        }
        
        @keyframes logoScroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </section>
  );
}
